import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath, getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BrandStateContext, getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COMMON } from "@tCentro/theme/design";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.maximumNumberOfBrandsPerRow]: string;
    };
    categoryId: string;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    const { productFilters } = state.pages.productList;
    return {
        query: location.search,
        catalogPageState,
        brandPath,
        productsDataView: getProductListDataView(state),
        categoryFacets: getProductListDataViewProperty(state, "properties"),
        productFiltersQuery: productFilters.query,
        breadCrumbs: state.components.breadcrumbs.links,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext &
    HasCategoryContext;

const mapDispatchToProps = {};

export interface BrandListStyles {
    infoCardTitle?: TypographyProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    link?: LinkProps;
    image?: LazyImageProps;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    buttonLink?: LinkPresentationProps;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    subcategoryNameStyles?: InjectableCss;
    subcategoryDescriptionStyles?: InjectableCss;
    subCategoryContentContainer: InjectableCss;
    categoryNameTitleStyle?: InjectableCss;
    imglinkStyles?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    imglinkStyles: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    categoryNameTitleStyle: {
        css: css`
            font-family: Poppins;
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            color: ${getProp("theme.typography.p.color")};
            margin-bottom: 16px;
        `,
    },
    subCategoryContentContainer: {
        css: css`
            padding: 24px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: -4px;
        `,
    },
    subcategoryNameStyles: {
        css: css`
            max-width: 137px;
            span {
                font-family: Poppins;
                font-size: 16px;
                font-weight: 600;
                line-height: 24px;
                text-align: left;
                color: ${getProp("theme.typography.p.color")} !important;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                overflow: hidden;
            }
        `,
    },
    subcategoryDescriptionStyles: {
        css: css`
            -webkit-line-clamp: 2;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            p {
                font-family: Poppins;
                font-size: 12px;
                font-weight: 400;
                line-height: 16px;
                // margin-top: 16px;
                color: ${getProp("theme.typography.p.color")} !important;
            }
        `,
    },
    image: {
        css: css`
            justify-content: flex-start;
            height: 100%;
            img {
                max-height: 100px;
                object-fit: contain;
                width: 100%;
                border-bottom: 1px solid #e0dfe0;
                height: 100%;
                @media (min-width: 980px) and (max-width: 1080px) {
                    max-height: 100%;
                    height: auto;
                }
            }
        `,
    },
    infoCardTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            font-size: 1.5rem;
            line-height: 33px;
            margin-bottom: 0rem;
            padding-top: 20px;
        `,
    },
    container: {
        css: css`
            margin: 20px 0 20px 0;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 1rem;
                        `,
                        null,
                        null,
                        css`
                            padding: 0 5rem;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wrapper: {
        css: css`
            /* background-color: #ebebeb; */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100% !important;
                        /* padding: 20px; */
                        flex-wrap: wrap;
                    `,
                    css`
                        max-width: 100% !important;
                        /* padding: 20px; */
                        flex-wrap: wrap;
                    `,
                    css`
                        /* max-width: 720px !important; */
                        flex-wrap: wrap;
                    `,
                    css`
                        /* max-width: 917px !important; */
                        padding: 0px;
                        flex-wrap: nowrap;
                    `,
                    css`
                        /* max-width: 1300px !important; */
                        flex-wrap: nowrap;
                        padding: 0px;
                    `,
                ])}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            z-index: 999;
            top: 50%;
            transform: translateY(-50%);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 12.33% !important;
                        max-width: 12.33% !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: ${getColor("primary")};
            border-radius: 50%;
            position: relative;
            min-height: auto !important;
            width: 34px;
            height: 34px;
            span {
                width: 100%;
                height: 100%;
                border-radius: 50%;
            }
            svg {
                width: 30px;
                height: 30px;
                stroke: white;
                position: absolute;
                top: 1px;
                left: 0px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",

        css: css`
            position: absolute;
            z-index: 999;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 12.33% !important;
                        max-width: 12.33% !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    nextArrowButton: {
        // variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: ${getColor("primary")};
            border-radius: 50%;
            position: relative;
            min-height: auto !important;
            width: 34px;
            height: 34px;
            span {
                width: 100%;
                height: 100%;
                border-radius: 50%;
            }
            svg {
                width: 30px;
                height: 30px;
                stroke: white;
                position: absolute;
                top: 1px;
                left: 3px;
            }
            &:focus {
                outline: none;
            }
            &:hover {
                background: ${getColor("primary")};
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            &:after {
                content: "";
                position: absolute;
                z-index: 1;
                width: 100px;
                box-sizing: border-box;
                right: 0px;
                height: 100%;
                background: linear-gradient(270deg, #f2f2f2 0%, rgba(242, 242, 242, 0) 100%);
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            /* padding: 30px 7px 13px; */
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            /* padding-top: 10px; */
            gap: 10px;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            max-width: 185px;
            width: 100%;
            border: 4px solid #e0dfe0;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            /* cursor: pointer; */
            position: relative;
            justify-content: flex-start;
            background-color: white;
        `,
    },
    imageContainer: {
        css: css`
            width: 100%;
            span {
                height: 100%;
                width: 100%;
            }
            div[class^="LazyImageStyle"] {
                width: 100%;
            }
            span[class^="IconWrapper"] {
                width: 100%;
                justify-content: center;
            }
        `,
    },
};

const styles = listStyles;

const CENSearchCategoryProductListingCarousel: FC<Props> = ({
    fields,
    shellContext,
    theme,
    catalogPageState,
    query,
    brandPath,
    productsDataView,
    categoryFacets,
    productFiltersQuery,
    breadCrumbs,
    category
}) => {
    const categoryFacetsData = JSON.parse(categoryFacets?.categoryList ?? "[]");

    const lastLevelCategory = breadCrumbs?.[breadCrumbs?.length - 1]?.children;

    const categoryFacetsDataNew = categoryFacetsData.filter((item: any) => item.Name !== lastLevelCategory);

    const brandName = catalogPageState.value?.title?.includes("|")
        ? catalogPageState.value?.title?.split("|")?.[0]
        : catalogPageState?.value?.title;

    const isBrandPath = brandPath?.toLowerCase()?.includes("/brands");

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const scrollTo = React.useCallback((index: number) => embla?.scrollTo(index), [embla]);
    const scrollPrev = React.useCallback(() => embla?.scrollPrev(), [embla]);
    const scrollNext = React.useCallback(() => embla?.scrollNext(), [embla]);

    React.useEffect(() => {
        embla?.scrollTo(0, true);
    }, [productFiltersQuery]);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = Number(fields.maximumNumberOfBrandsPerRow) || 5;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;

        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else if (window.innerWidth < theme.breakpoints.values[5]) {
            localSlidesToScroll = 5;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maximumNumberOfBrandsPerRow]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maximumNumberOfBrandsPerRow, windowResizeTime]);

    React.useEffect(() => {
        if (!embla || !categoryFacetsData) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !categoryFacetsData) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, categoryFacetsData]);

    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
        },
    };

    const isBot = getIsWebCrawler();
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);

    return (
        <>
            {((productFiltersQuery && productsDataView.value?.length !== 0) || isBrandPath) && (
                <StyledWrapper {...styles.wrapper}>
                    {isBrandPath && brandName && categoryFacetsDataNew?.length !== 0 && (
                        <StyledWrapper as={category?.shortDescription ? "div" : "h1"} {...styles.categoryNameTitleStyle}>
                            {/* {translate(`Category from ${brandName}`)} {`(${categoryFacetsData?.length})`} */}
                            {siteMessage(
                                "CategoryBrandTitleTaxonomyCarousel",
                                `${brandName}`,
                                `${categoryFacetsDataNew?.length}`,
                            )}
                        </StyledWrapper>
                    )}
                    {!isBrandPath && productFiltersQuery && (
                        <StyledWrapper {...styles.categoryNameTitleStyle}>
                            {siteMessage(
                                "CategorySearchTitleTaxonomyCarousel",
                                `${productFiltersQuery}`,
                                `${categoryFacetsDataNew?.length}`,
                            )}
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.mainContainer}>
                        {categoryFacetsDataNew?.length > slidesToScroll && canScrollPrev && (
                            <GridItem {...styles.prevArrowGridItem}>
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => embla && embla.scrollPrev()}
                                    disabled={!canScrollPrev}
                                    data-test-selector="prevBtn"
                                >
                                    <ButtonIcon src={ChevronLeft} />
                                </Button>
                            </GridItem>
                        )}

                        <GridItem {...styles.carouselGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                        {categoryFacetsDataNew?.map((item: any, index: number) => (
                                            <StyledWrapper
                                                {...styles.carouselSlide}
                                                 
                                                key={`${item.Name}${index}`}
                                                style={{ width: `calc(100% / ${slidesToScroll})` }}
                                            >
                                                <StyledWrapper
                                                    {...styles.carouselSlideInner}
                                                    data-test-selector="carousel_slide"
                                                >
                                                    <StyledWrapper {...styles.imageContainer}>
                                                        <Link href={`${item?.Path}`} {...styles.imglinkStyles}>
                                                            <LazyImage
                                                                src={
                                                                    item.ImagePath?.length > 0
                                                                        ? item.ImagePath
                                                                        : COMMON?.defaultCategoryImagePath
                                                                }
                                                                {...styles.image}
                                                                altText={item.Name}
                                                            />
                                                        </Link>
                                                    </StyledWrapper>
                                                    <StyledWrapper {...styles.subCategoryContentContainer}>
                                                        <Link href={`${item?.Path}`} {...styles.subcategoryNameStyles}>
                                                            {item.Name}
                                                        </Link>
                                                        {isBot ? (
                                                            <Typography
                                                                css={css`
                                                                    -webkit-line-clamp: 2;
                                                                    display: -webkit-box;
                                                                    -webkit-box-orient: vertical;
                                                                    overflow: hidden;
                                                                    text-overflow: ellipsis;
                                                                    p {
                                                                        font-family: Poppins;
                                                                        font-size: 12px;
                                                                        font-weight: 400;
                                                                        line-height: 16px;
                                                                        text-align: left;
                                                                        // margin-top: 16px;
                                                                        color: ${getProp(
                                                                            "theme.typography.p.color",
                                                                        )} !important;
                                                                    }
                                                                `}
                                                            >
                                                                {parse(
                                                                    transformContentHtml(item?.HtmlContent),
                                                                    parserOptions,
                                                                )}
                                                            </Typography>
                                                        ) : isMounted ? (
                                                            <Typography as="p" {...styles.subcategoryDescriptionStyles}>
                                                                {parse(
                                                                    transformContentHtml(item?.HtmlContent),
                                                                    parserOptions,
                                                                )}
                                                            </Typography>
                                                        ) : null}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        {categoryFacetsDataNew?.length > slidesToScroll && canScrollNext && (
                            <GridItem {...styles.nextArrowGridItem}>
                                <Button
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                    disabled={!canScrollNext}
                                    data-test-selector="nextBtn"
                                >
                                    <ButtonIcon src={ChevronRight} />
                                </Button>
                            </GridItem>
                        )}
                    </GridContainer>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withIsInShell(withCategory(withTheme(CENSearchCategoryProductListingCarousel)))),
    definition: {
        displayName: " CEN Search Category Product Listing Carousel",
        fieldDefinitions: [
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "5",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "General",
                isVisible: item => false,
            },
        ],
        group: "Categories",
    },
};

export default widgetModule;
